import {
    faBellConcierge,
    faCircleDollarToSlot,
    faCog,
    faMoneyBill1Wave,
    faPaw,
    faStore,
    faUsers
} from '@fortawesome/pro-light-svg-icons';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { MarketplaceSchedule } from '@spike/model';
import { fetchOnboardingStepsThunk } from 'actions/onboardingSteps/OnboardingStepsActions';
import {
    openBusinessSettingsSidebar,
    closeBusinessSettingsSidebar
} from 'actions/businessSettingsSidebar/BusinessSettingsSidebarActions';
import clsx from 'clsx';
import Sidebar, { SidebarItem, SidebarSection } from 'components/UI/Sidebar/Sidebar';
import Marketplace, {
    MarketplaceBasics,
    MarketplaceGrooming,
    MarketplaceIntegrations,
    MarketplaceLogos,
    MarketplaceNotifications,
    MarketplacePetTypes,
    MarketplaceSpeciesRequirements,
    MarketplaceSetting2,
    MarketplaceStaffTraining,
    MarketplaceTax,
    MarketplaceTip,
    SETTINGS_FALSE_VALUE,
    SETTINGS_TRUE_VALUE
} from '@spike/marketplace-model';
import { FunctionComponent, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import {
    MarketplaceDepositSettings,
    MarketplaceStatus,
    fetchOrganizationMarketplacesThunk,
    fetchSettingsMarketplaceThunk,
    saveDepositSettingsThunk,
    saveSettingsMarketplaceThunk
} from '@spike/marketplace-action';
import { RootState } from 'store';
import AdditionalInfo from './AdditionalInfo';
import Amenities from './Amenities';
import Basics from './Basics';
import Deposit from './Deposit';
import EmailNotifications from './EmailNotifications';
import Quickbooks from './Integrations/Quickbooks';
import Integrations from './Integrations';
import OnlineBooking from './OnlineBooking';
import Logos from './Logos';
import PetRequirements from './PetRequirements';
import PetTypes from './PetTypes';
import Schedule from './Schedule';
import StaffTraining from './StaffTraining';
import Taxes from './Taxes';
import Tips from './Tips';
import { getEnrollmentStatusThunk } from '@spike/payments-action';
import { setSelectedSettingsMarketplaceId } from 'api/settingsMarketplaceSelection';
import {
    useHasPremiumPlan,
    usePremiumPlan,
    useAuth,
    useSettingsApiClientWrapper,
    BusinessSettingsMarketplaceProvider
} from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Select from 'components/UI/V3/Forms/Select';
import Spinner from 'components/UI/V3/Spinner';
import Analytics from 'analytics/client';
import { Events } from 'analytics/events';
import { setTimeZone } from 'actions/login/LoginActions';
import { businessBasicSettingsHubspot } from 'actions/hubspot/HubSpotActions';
import { Plans } from './Plans/Plans';
import { Billing } from './Billing/Billing';
import TextMessages from './TextMessages';
import IntakeForm from 'components/Marketplace/BusinessSettings/IntakeForm/IntakeForm';
import { ScheduleType } from 'components/Marketplace/BusinessSettings/ScheduleType/ScheduleType';
import Permissions from 'components/Marketplace/BusinessSettings/Permissions';

import Rooms from './Rooms';
import Locations from './Locations';
import Feeding from './Feeding';
import { FeedingMoments } from './Feeding/Feeding';
import Belongings from './Belongings';
import PetParentPortal from './PetParentPortal';
import Tags from './Tags/Tags/Tags';
import Invoices from './Invoices';
import PaymentProcessing from './PaymentsProcessing';
import { isSettingEnabled } from 'utils/SettingsUtils';

export interface Props {
    className?: string;
}

const settingsSaveSuccessStatuses = new Set<MarketplaceStatus>([
    MarketplaceStatus.SaveSuccess,
    MarketplaceStatus.SaveSettingsMarketplaceSuccess,
    MarketplaceStatus.ChangeOlbStatusSuccess,
    MarketplaceStatus.SaveOlbLimitsSuccess,
    MarketplaceStatus.SaveOlbAllowSelectStaffSuccess,
    MarketplaceStatus.SaveOlbAllowNewClientsSuccess,
    MarketplaceStatus.SaveScheduleTypeSuccess,
    MarketplaceStatus.SaveIntakeFormQuestionsSuccess,
    MarketplaceStatus.SaveOlbGaTrackingIdSuccess,
    MarketplaceStatus.SaveOlbGtmTrackingIdSuccess,
    MarketplaceStatus.SavePermissionsSuccess,
    MarketplaceStatus.SaveCustomDisclaimerSuccess,
    MarketplaceStatus.SaveOlbRequireVaccinesSuccess,
    MarketplaceStatus.SaveBrandColorsSuccess,
    MarketplaceStatus.SaveOlbBookingRequestCategoriesSuccess,
    MarketplaceStatus.SavePppCancellationSuccess,
    MarketplaceStatus.SaveCardOnFileSuccess,
    MarketplaceStatus.SaveIntakeFormCardOnFileSuccess,
    MarketplaceStatus.SaveServiceCategoriesSuccess,
    MarketplaceStatus.SaveDepositSettingsSuccess
]);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%',
            minHeight: '100%',
            height: 'max-content',
            borderTop: '2px solid #F1F1F1',
            background: 'white'
        },
        sidebarContainer: {
            zIndex: 50,
            display: 'none',
            flex: '0 0 157px',
            paddingTop: '21px',
            paddingLeft: '35px',
            paddingRight: '20px',
            position: 'relative',
            backgroundColor: '#ffffff',
            borderRight: '2px solid #F1F1F1',
            maxHeight: '94vh',
            overflowY: 'auto',
            [theme.breakpoints.up('sm')]: {
                display: 'flex'
            },
            [theme.breakpoints.down('sm')]: {
                width: '100vw',
                paddingTop: '13px',
                paddingLeft: '15px',
                paddingRight: '15px',
                position: 'fixed',
                left: '-100%',
                visibility: 'hidden',
                top: '50px',
                height: 'calc(100vh - 50px)',
                background: '#fff',
                zIndex: 9,
                transition: 'all .3s',
                overflowY: 'scroll',
                paddingBottom: '180px',
                display: 'block'
            },
            [theme.breakpoints.only('md')]: {
                paddingTop: 20,
                paddingLeft: 20,
                paddingRight: 20
            },
            [theme.breakpoints.only('lg')]: {
                flex: '0 0 230px',
                paddingTop: 28,
                paddingLeft: 24,
                paddingRight: 24
            },
            [theme.breakpoints.only('xl')]: {
                flex: '0 0 316px',
                paddingTop: 42,
                paddingLeft: 32,
                paddingRight: 32
            }
        },
        sidebarContainerDefault: {
            left: '0px !important',
            transition: 'none'
        },
        sidebarContainerMain: {
            transition: 'none !important'
        },
        sidebarContainerVisible: {
            [theme.breakpoints.down('sm')]: {
                left: '0px !important',
                visibility: 'visible !important'
            }
        },
        bodyContainer: {
            width: '100%',
            display: 'flex',
            paddingTop: 19,
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 19,
            [theme.breakpoints.up('sm')]: {
                paddingTop: 31,
                paddingLeft: 25,
                paddingRight: 25
            },
            [theme.breakpoints.up('md')]: {
                paddingTop: 41,
                paddingLeft: 45,
                paddingRight: 45
            }
        },
        bodyContainerNew: {
            width: '100%',

            [theme.breakpoints.down('sm')]: {
                paddingTop: '20px',
                paddingLeft: '15px',
                paddingRight: '15px',
                paddingBottom: '20px'
            }
        },
        bodyContainerRooms: {
            width: '100%'
        },
        loadingContainer: {
            [theme.breakpoints.down('sm')]: {
                opacity: 0
            }
        },
        hiddenOnMobile: {
            opacity: 0
        },
        locationHeader: {
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 20,
            minWidth: 0
        },
        locationSelect: {
            minWidth: 0,
            width: '100%'
        },
        locationSelectTrigger: {
            '&&': {
                height: 'auto',
                minWidth: 0,
                padding: 0,
                border: 'none',
                borderRadius: 0,
                backgroundColor: 'transparent',
                fontWeight: 700,
                [theme.breakpoints.down('lg')]: {
                    fontSize: 15
                },
                [theme.breakpoints.only('xl')]: {
                    fontSize: 18
                }
            },
            '& > div:first-child': {
                display: 'block',
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                fontWeight: 700
            },
            '& > svg': {
                flexShrink: 0
            }
        },
        locationAddress: {
            marginTop: 8,
            fontWeight: 300,
            lineHeight: 1.3,
            [theme.breakpoints.down('md')]: {
                fontSize: 12
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: 11
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: 16
            }
        },
        settingsSpinner: {
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320
        }
    })
);

const enabledDeposits: boolean = process.env.REACT_APP_DEPOSITS_ENABLED?.toLowerCase() === 'true';
const DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY = 'default_tax_for_custom_items_id';
const NO_TAX_SETTING_VALUE = '0';

const formatLocationAddress = (address?: {
    street?: string;
    suite?: string;
    city?: string;
    state?: string;
    zipcode?: string;
}) => {
    if (!address) {
        return '';
    }

    const street = [address.street, address.suite].filter(Boolean).join(' ');
    const cityLine = [address.city, address.state, address.zipcode].filter(Boolean).join(' ');

    return [street, cityLine].filter(Boolean).join(', ');
};

const updateDefaultCustomItemsTaxSetting = (
    settings: Array<MarketplaceSetting2>,
    defaultTaxValue: string
): Array<MarketplaceSetting2> =>
    settings.map(setting =>
        setting.key === DEFAULT_TAX_FOR_CUSTOM_ITEMS_SETTING_KEY
            ? {
                  ...setting,
                  value: (defaultTaxValue && defaultTaxValue !== NO_TAX_SETTING_VALUE
                      ? defaultTaxValue
                      : null) as unknown as string
              }
            : setting
    );

export const BusinessSettingsComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const location = useLocation();
    const selectedPath = pathname.split('/').slice(1)[2];
    const theme = useTheme();
    const dispatch = useDispatch();
    const activeSubscription = usePremiumPlan();
    const isRoomsEnabled = process.env.REACT_APP_ROOMS_ENABLED?.toLowerCase() === 'true';

    const [bodyClass, setBodyClass] = useState('');
    const [hide, setHide] = useState(true);
    const [loading, setLoading] = useState(true);

    const isOpenBusinessSettingsSideBar = useSelector(
        (state: RootState) => state.BusinessSettingsSidebar.isOpenBusinessSettingsSideBar
    );

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const openSidebar = () => {
        dispatch(openBusinessSettingsSidebar());
    };

    const closeSidebar = () => {
        dispatch(closeBusinessSettingsSidebar());
    };

    const auth = useAuth();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const [selectedMarketplaceId, setSelectedMarketplaceId] = useState<number | undefined>(auth.marketplaceId);
    const [settingsLoading, setSettingsLoading] = useState(true);
    setSelectedSettingsMarketplaceId(selectedMarketplaceId);
    const apiClientWrapper = useSettingsApiClientWrapper();

    const marketplace = useSelector<RootState, Marketplace>(state => state.marketplace.settingsMarketplace);

    useEffect(() => {
        if (selectedMarketplaceId == null && auth.marketplaceId != null) {
            setSelectedMarketplaceId(auth.marketplaceId);
        }
    }, [auth.marketplaceId, selectedMarketplaceId]);

    useEffect(() => {
        if (selectedMarketplaceId == null) {
            setSettingsLoading(false);
            return;
        }

        let active = true;
        setSettingsLoading(true);

        const waitFor = (request: unknown) => (request instanceof Promise ? request : Promise.resolve());
        const enrollmentRequest = dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        const marketplaceRequest = dispatch(fetchSettingsMarketplaceThunk(apiClientWrapper, selectedMarketplaceId));

        Promise.all([waitFor(enrollmentRequest), waitFor(marketplaceRequest)]).finally(() => {
            if (active) {
                setSettingsLoading(false);
            }
        });

        return () => {
            active = false;
        };
        // Reload the selected location and its psp_status when settings opens and whenever the combo changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMarketplaceId]);

    useEffect(() => {
        return () => setSelectedSettingsMarketplaceId(undefined);
    }, []);

    const hasPremiumPlan = useHasPremiumPlan();

    const isDaycareEnabled = isSettingEnabled(marketplace.settings, 'daycare_enabled');

    const isBoardingEnabled = isSettingEnabled(marketplace.settings, 'boarding_enabled');

    const basicsItem = { id: 'basics', name: 'Basics' };
    const locationsItem = { id: 'locations', name: 'Locations', isNew: true };
    const additionalInfoItem = { id: 'additional', name: 'Service Categories' };
    const roomsItem = { id: 'rooms', name: 'Rooms & Spaces', isNew: true };
    const plansItem = { id: 'plans', name: 'Plans' };
    const billingItem = { id: 'billing', name: 'Billing' };
    const scheduleItem = { id: 'hours', name: 'Hours' };
    const logoItem = { id: 'logo', name: 'Logo' };
    const onlineBookingItem = { id: 'onlineBooking', name: 'Online Booking' };
    const textMessagesItem = { id: 'textMessages', name: 'Text Messages' };

    const scheduleTypeItem = {
        id: 'schedule-type',
        name: 'Schedule Type',
        isNew: true
    };
    const permissionsItem = { id: 'permissions', name: 'Permissions', isNew: true };

    // const intakeFormItem = {
    //     id: 'intake-form',
    //     name: 'Intake Form',
    //     isNew: true
    // };

    const requirementsItem = { id: 'requirements', name: 'Requirements' };
    const typesItem = { id: 'types', name: 'Types' };
    const petParentPortalItem = { id: 'pet-parent-portal', name: 'Pet Parent Portal', isNew: true };
    const feedingItem = { id: 'feeding', name: 'Task Schedule', isNew: true };
    const belongingsItem = { id: 'belongings', name: 'Belongings', isNew: true };
    const tagsItem = { id: 'tags', name: 'Tags', isNew: true };

    const taxesItem = { id: 'taxes', name: 'Taxes' };
    const tipsItem = { id: 'tips', name: 'Tips' };
    const depositItem = { id: 'deposit', name: 'Deposits' };
    const paymentProcessingItem = { id: 'processing', name: 'Payment Processing' };

    const emailItem = { id: 'email', name: 'Notifications' };
    //const dataExportItem = { id: 'data-export', name: 'Data Export' };
    const integrationsItem = { id: 'integrations', name: 'Integrations' };

    const amenitiesItem = { id: 'amenities', name: 'Amenities' };
    const trainingItem = { id: 'training', name: 'Staff Trainning' };

    const invoicesItem = {
        id: 'invoices',
        name: 'Invoices',
        isNew: true
    };

    const [selectedSidebarItem, setSelectedSidebarItem] = useState(basicsItem);
    const [logosSelected, setLogosSelected] = useState(marketplace.logos);
    const logosAuxiliar = marketplace.logos;

    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const getbusinessInfoItems = () => {
        const items = [
            basicsItem,
            locationsItem,
            additionalInfoItem,
            roomsItem,
            plansItem,
            textMessagesItem,
            billingItem,
            scheduleItem,
            logoItem
        ];

        let filteredItems = items.filter(item => item.id !== 'rooms' || isRoomsEnabled);

        if (!hasPremiumPlan) {
            filteredItems = filteredItems.filter(item => item.id !== 'textMessages');
        }

        return filteredItems;
    };

    const businessInfoItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'BUSINESS INFO',
        path: 'info',
        icon: faStore,
        items: getbusinessInfoItems()
    };

    const bookingsItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'BOOKINGS',
        path: 'bookings',
        icon: faBellConcierge,
        items: [scheduleTypeItem, onlineBookingItem]
    };

    const staffItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'STAFF',
        path: 'staff',
        icon: faUsers,
        items: [permissionsItem]
    };

    // const clientsItems: SidebarSection = {
    //     onclick: closeSidebar,
    //     toggleClassOnTitle: true,
    //     sectionAccordion: true,
    //     title: 'CLIENTS',
    //     path: 'clients',
    //     icon: faUsers,
    //     items: [intakeFormItem]
    // };

    const petsItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'CLIENTS & PETS',
        path: 'pets',
        icon: faPaw,
        items:
            isDaycareEnabled || isBoardingEnabled
                ? [requirementsItem, typesItem, petParentPortalItem, belongingsItem, tagsItem, feedingItem]
                : [requirementsItem, typesItem, petParentPortalItem, belongingsItem, tagsItem]
    };

    const paymentsItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'PAYMENTS',
        path: 'payments',
        icon: faMoneyBill1Wave,
        items: [paymentProcessingItem]
    };

    const salesItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'SALES',
        path: 'sales',
        icon: faCircleDollarToSlot,
        items: enabledDeposits ? [taxesItem, tipsItem, depositItem, invoicesItem] : [taxesItem, tipsItem, invoicesItem]
    };

    const facilityAndStaffItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'FACILITY & STAFF',
        path: 'facilityStaff',
        icon: faUsers,
        items: [amenitiesItem, trainingItem]
    };

    const settingsItems: SidebarSection = {
        onclick: closeSidebar,
        toggleClassOnTitle: true,
        sectionAccordion: true,
        title: 'SETTINGS',
        path: 'settings',
        icon: faCog,
        items: [emailItem, integrationsItem]
    };

    useEffect(() => {
        hide && setTimeout(() => setHide(false), 500);
    }, []);

    useEffect(() => {
        // Check if the query parameter "?open=settings" is present
        const isOpenFromQueryParam = location.search.includes('open=settings');

        dispatch(isOpenFromQueryParam ? openBusinessSettingsSidebar() : closeBusinessSettingsSidebar());
        setBodyClass(isOpenFromQueryParam ? 'prevent-scroll' : '');

        setLoading(false);
    }, [location.search, dispatch]);

    useEffect(() => {
        const body = document.body;
        body.classList.toggle('prevent-scroll', isOpenBusinessSettingsSideBar);
        body.style.overflow = isOpenBusinessSettingsSideBar ? 'hidden' : '';
        body.style.height = isOpenBusinessSettingsSideBar ? '100%' : '';

        return () => {
            body.style.overflow = '';
            body.style.height = '';
            body.classList.remove('your-class-name');
        };
    }, [isOpenBusinessSettingsSideBar]);

    useEffect(() => {
        setLogosSelected(logosAuxiliar);
    }, [logosAuxiliar, selectedSidebarItem]);

    const selectSidebareItemHandler = (item: SidebarItem) => {
        setSelectedSidebarItem(item);
    };

    // Ref to indicate the current save operation qualifies as the FIRST time business hours are being set up.
    const hoursFirstSaveRef = useRef(false);
    // Read onboarding completion flag so we can know if hours were already configured.
    const businessHoursDone = useSelector<RootState, boolean>(
        state => state.onboardingSteps.onboardingSteps.businessHoursDone
    );

    useEffect(() => {
        if (settingsSaveSuccessStatuses.has(marketplaceStatus)) {
            dispatch(fetchOrganizationMarketplacesThunk(apiClientWrapper));
        }

        if (
            marketplaceStatus === MarketplaceStatus.SaveSuccess ||
            marketplaceStatus === MarketplaceStatus.SaveSettingsMarketplaceSuccess
        ) {
            dispatch(fetchOnboardingStepsThunk());

            // One-shot tracking: if this save was marked as the first hours setup and we have not yet persisted the event.
            try {
                const persistentKey = `hours_onboarding_tracked_${marketplace.id}`;
                const alreadyTrackedPersistent = localStorage.getItem(persistentKey) === '1';
                if (hoursFirstSaveRef.current && !alreadyTrackedPersistent) {
                    Analytics.track(Events.HOURS_SAVED_ONBOARDING, {
                        marketplace_id: marketplace.id,
                        user_id: auth?.user?.id
                    });
                    try {
                        localStorage.setItem(persistentKey, '1');
                    } catch (e) {
                        console.warn(e);
                    }
                }
            } catch (e) {
                console.warn(e);
            } finally {
                // Reset ref after handling success path (or failure) to avoid accidental future triggers.
                hoursFirstSaveRef.current = false;
            }
        }
        // eslint-disable-next-line
    }, [marketplaceStatus]);

    const setSettingsTimeZone = (timeZone: string, marketplaceId: number) => {
        if (marketplaceId === auth.marketplaceId) {
            setTimeZone(timeZone, marketplaceId, dispatch);
        }
    };

    const save = (marketplaceToSave: Marketplace) => {
        dispatch(saveSettingsMarketplaceThunk(apiClientWrapper, marketplaceToSave, setSettingsTimeZone));
    };

    const saveBasicsHandler = (basics: MarketplaceBasics) => {
        save({ ...marketplace, basics });
        dispatch(businessBasicSettingsHubspot(basics));
    };

    const saveFeedingHandler = (feeding: FeedingMoments) => {
        const newMarketplace = { ...marketplace };

        const DEFAULT_FEEDING_TIMES = {
            morning: '0800',
            afternoon: '1300',
            evening: '1700'
        };

        if (newMarketplace.settings) {
            newMarketplace.settings = newMarketplace.settings.map(setting => {
                const morningTime = feeding.morning.selected
                    ? feeding.morning.time?.replace(':', '')
                    : DEFAULT_FEEDING_TIMES.morning;

                const afternoonTime = feeding.afternoon.selected
                    ? feeding.afternoon.time?.replace(':', '')
                    : DEFAULT_FEEDING_TIMES.afternoon;

                const eveningTime = feeding.evening.selected
                    ? feeding.evening.time?.replace(':', '')
                    : DEFAULT_FEEDING_TIMES.evening;

                if (setting.key === 'morning_feeding_time') {
                    return {
                        ...setting,
                        value: morningTime
                    };
                }

                if (setting.key === 'morning_feeding_enabled') {
                    const enabledValue = feeding.morning.selected ? SETTINGS_TRUE_VALUE : SETTINGS_FALSE_VALUE;
                    return {
                        ...setting,
                        value: enabledValue
                    };
                }

                if (setting.key === 'afternoon_feeding_time') {
                    return {
                        ...setting,
                        value: afternoonTime
                    };
                }

                if (setting.key === 'afternoon_feeding_enabled') {
                    const enabledValue = feeding.afternoon.selected ? SETTINGS_TRUE_VALUE : SETTINGS_FALSE_VALUE;
                    return {
                        ...setting,
                        value: enabledValue
                    };
                }

                if (setting.key === 'evening_feeding_time') {
                    return {
                        ...setting,
                        value: eveningTime
                    };
                }

                if (setting.key === 'evening_feeding_enabled') {
                    const enabledValue = feeding.evening.selected ? SETTINGS_TRUE_VALUE : SETTINGS_FALSE_VALUE;
                    return {
                        ...setting,
                        value: enabledValue
                    };
                }

                return setting;
            });
        }

        save(newMarketplace);
    };

    const saveIntegrationsHandler = (integrations: MarketplaceIntegrations) => {
        save({ ...marketplace, integrations });
    };

    const saveScheduleHandler = (schedule: MarketplaceSchedule) => {
        // Mark this save as first-time setup if onboarding says hours not done yet AND event not tracked.
        try {
            const persistentKey = `hours_onboarding_tracked_${marketplace.id}`;
            const alreadyTrackedPersistent = localStorage.getItem(persistentKey) === '1';
            if (!alreadyTrackedPersistent && !businessHoursDone) {
                hoursFirstSaveRef.current = true;
            }
        } catch (e) {
            console.warn(e);
        }
        save({ ...marketplace, schedule });
    };

    const saveLogosHandler = (logos: MarketplaceLogos) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.logos = { ...logos };
        save(newMarketplace);
    };

    const saveRequirementsHandler = (requirements: MarketplaceSpeciesRequirements) => {
        const payload = {
            ...marketplace,
            requirements
        };
        save(payload);
    };

    const savePetTypesHandler = (petTypes: MarketplacePetTypes) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.petTypes = { ...petTypes };
        save(newMarketplace);
    };

    const saveTaxesHandler = (taxes: Array<MarketplaceTax>, defaultCustomItemsTaxValue: string) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.taxes = [...taxes];
        newMarketplace.settings = updateDefaultCustomItemsTaxSetting(
            newMarketplace.settings,
            defaultCustomItemsTaxValue
        );
        save(newMarketplace);
    };

    const saveTipsHandler = (tips: Array<MarketplaceTip>) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.tips = [...tips];
        save(newMarketplace);
    };

    const savePetPositivePersonalityHandler = (petPositivePersonality: Array<string>) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.petPositivePersonality = [...petPositivePersonality];
        save(newMarketplace);
    };

    const savePetNegativePersonalityHandler = (petNegativePersonality: Array<string>) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.petNegativePersonality = [...petNegativePersonality];
        save(newMarketplace);
    };

    const saveClientPositivePersonalityHandler = (clientPositivePersonality: Array<string>) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.clientPositivePersonality = [...clientPositivePersonality];
        save(newMarketplace);
    };

    const saveClientNegativePersonalityHandler = (clientNegativePersonality: Array<string>) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.clientNegativePersonality = [...clientNegativePersonality];
        save(newMarketplace);
    };

    const saveDepositHandler = (settings: MarketplaceDepositSettings) => {
        dispatch(saveDepositSettingsThunk(apiClientWrapper, settings));
    };

    const saveGroomingHandler = (grooming: MarketplaceGrooming) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.grooming = { ...grooming };
        save(newMarketplace);
    };

    const saveStaffTrainingHandler = (staffTraining: MarketplaceStaffTraining) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.staffTraining = { ...staffTraining };
        save(newMarketplace);
    };

    const onChangeLogosHandler = (logos: MarketplaceLogos) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.logos = { ...logos };
        setLogosSelected(newMarketplace.logos);
    };

    const saveNotificationsHandler = (
        notifications: MarketplaceNotifications,
        settings: Array<MarketplaceSetting2>
    ) => {
        save({
            ...marketplace,
            notifications: { ...notifications },
            settings: [...settings]
        });
    };

    const savePricingOptionHandler = (pricingOption: string) => {
        const newMarketplace = { ...marketplace };
        newMarketplace.onlineBooking.pricingOption = pricingOption as 'starting_at' | 'price';
        save(newMarketplace);
    };

    const saveTipBreakdownEnabledHandler = (tipBreakdownEnabled: boolean) => {
        const newMarketplace = { ...marketplace };
        //newMarketplace.tipBreakdownEnabled = tipBreakdownEnabled;
        newMarketplace.settings = newMarketplace.settings.map(setting => {
            if (setting.key === 'tip_breakdown_enabled') {
                return {
                    ...setting,
                    value: tipBreakdownEnabled ? SETTINGS_TRUE_VALUE : SETTINGS_FALSE_VALUE
                };
            }
            return setting;
        });
        save(newMarketplace);
    };

    const locationOptions = useMemo(
        () =>
            [...organizationMarketplaces]
                .sort((left, right) => left.name.localeCompare(right.name))
                .map(location => ({
                    label: location.name,
                    value: String(location.id)
                })),
        [organizationMarketplaces]
    );

    const selectedOrganizationLocation = organizationMarketplaces.find(
        location => location.id === selectedMarketplaceId
    );
    const locationAddress =
        marketplace.id === selectedMarketplaceId ? marketplace.basics.address : selectedOrganizationLocation?.address;

    const showLocationCombo = locationOptions.length > 1;
    const locationHeader = showLocationCombo ? (
        <Box className={classes.locationHeader}>
            <Select
                label="Location"
                placeholder="Select a location"
                className={classes.locationSelect}
                triggerClassName={classes.locationSelectTrigger}
                options={locationOptions}
                value={selectedMarketplaceId != null ? String(selectedMarketplaceId) : undefined}
                onChange={value => {
                    const marketplaceId = Number(value);
                    if (marketplaceId === selectedMarketplaceId) {
                        return;
                    }
                    setSettingsLoading(true);
                    setSelectedMarketplaceId(marketplaceId);
                }}
            />
            {formatLocationAddress(locationAddress) && (
                <Typography className={classes.locationAddress}>{formatLocationAddress(locationAddress)}</Typography>
            )}
        </Box>
    ) : undefined;

    const sidebar = (
        <Sidebar
            alternativeTitle="Business Settings"
            title=""
            subtitle=""
            header={locationHeader}
            itemsDisabled={settingsLoading}
            sections={[
                businessInfoItems,
                staffItems,
                bookingsItems,
                petsItems,
                paymentsItems,
                salesItems,
                settingsItems
            ]}
            selectedItemId={selectedPath}
            onSelect={selectSidebareItemHandler}
            onClickItemHandler={isMobile ? closeSidebar : undefined}
            className="SidebarBusinessSettings"
        />
    );

    const petTypes = (
        <Box
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <PetTypes
                onSave={savePetTypesHandler}
                petTypes={marketplace.petTypes}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const petTags = (
        <Box
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <Tags
                onSavePetPositivePersonality={savePetPositivePersonalityHandler}
                onSavePetNegativePersonality={savePetNegativePersonalityHandler}
                onSaveClientPositivePersonality={saveClientPositivePersonalityHandler}
                onSaveClientNegativePersonality={saveClientNegativePersonalityHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const locations = (
        <div
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <Locations
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </div>
    );

    const basics = (
        <Box
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <Basics
                basics={{ ...marketplace.basics }}
                businessId={marketplace.id}
                onSave={saveBasicsHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const additionalInfo = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <AdditionalInfo
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const rooms = (
        <div
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <Rooms
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </div>
    );

    const feeding = (
        <div
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <Feeding onSave={saveFeedingHandler} />
        </div>
    );

    const petParentPortal = (
        <div
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <PetParentPortal
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </div>
    );

    const belongings = (
        <div
            className={clsx(classes.bodyContainer, classes.bodyContainerNew, {
                [classes.hiddenOnMobile]: hide && isMobile
            })}
        >
            <Belongings
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </div>
    );

    const plans = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <Plans
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const billing = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <Billing
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const textMessages = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <TextMessages
                openSidebar={openSidebar}
                isMobile={isMobile}
                subscription={activeSubscription}
            />
        </Box>
    );

    const schedule = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <Schedule
                schedule={{ ...marketplace.schedule }}
                enableSpecialHours={true}
                onSave={saveScheduleHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const logos = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <Logos
                basics={{ ...marketplace.basics }}
                logos={logosSelected}
                onChange={onChangeLogosHandler}
                onSave={saveLogosHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const onlineBooking = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <OnlineBooking
                openSidebar={openSidebar}
                isMobile={isMobile}
                onlineBooking={{ ...marketplace.onlineBooking }}
                logos={logosSelected}
                onChange={onChangeLogosHandler}
                onSave={saveLogosHandler}
                onSaveMarketplace={savePricingOptionHandler}
            />
        </Box>
    );

    // const intakeForm = (
    //     <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
    //         <IntakeForm
    //             isMobile={isMobile}
    //             openSidebar={openSidebar}
    //         />
    //     </Box>
    // );

    const requirements = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <PetRequirements
                requirements={{ ...marketplace.requirements }}
                onSave={saveRequirementsHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const taxes = (
        <Box className={classes.bodyContainer}>
            <Taxes
                openSidebar={openSidebar}
                isMobile={isMobile}
                taxes={[...marketplace.taxes]}
                onSave={saveTaxesHandler}
            />
        </Box>
    );

    const tips = (
        <Box className={classes.bodyContainer}>
            <Tips
                openSidebar={openSidebar}
                isMobile={isMobile}
                tips={[...marketplace.tips]}
                onSave={saveTipsHandler}
            />
        </Box>
    );

    const deposit = (
        <Box className={classes.bodyContainer}>
            <Deposit
                deposit={{ ...marketplace.deposit }}
                depositRules={marketplace.depositRules}
                depositCategories={marketplace.depositCategories}
                onSave={saveDepositHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const amenities = (
        <Box className={classes.bodyContainer}>
            <Amenities
                grooming={{ ...marketplace.grooming }}
                onSave={saveGroomingHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );
    const training = (
        <Box className={classes.bodyContainer}>
            <StaffTraining
                staffTraining={{ ...marketplace.staffTraining }}
                onSave={saveStaffTrainingHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const emailNotifications = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <EmailNotifications
                notifications={{ ...marketplace.notifications }}
                settings={[...marketplace.settings]}
                onSave={saveNotificationsHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const quickbooks = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <Quickbooks
                openSidebar={openSidebar}
                isMobile={isMobile}
                title="QuickBooks Integration"
                iconSrc="/images/settings/integrations/quickbooks.svg"
            />
        </Box>
    );

    const integrations = (
        <Box className={classes.bodyContainer}>
            <Integrations
                integrations={{ ...marketplace.integrations }}
                onSave={saveIntegrationsHandler}
                openSidebar={openSidebar}
                isMobile={isMobile}
            />
        </Box>
    );

    const scheduleType = (
        <Box className={classes.bodyContainerNew}>
            <ScheduleType
                isMobile={isMobile}
                openSidebar={openSidebar}
            />
        </Box>
    );
    const permissions = (
        <Box className={classes.bodyContainerNew}>
            <Permissions
                isMobile={isMobile}
                openSidebar={openSidebar}
            />
        </Box>
    );

    const invoices = (
        <Box className={classes.bodyContainer}>
            <Invoices
                openSidebar={openSidebar}
                isMobile={isMobile}
                onSaveMarketplace={saveTipBreakdownEnabledHandler}
            />
        </Box>
    );

    const paymentProcessing = (
        <Box className={clsx(classes.bodyContainer, classes.bodyContainerNew)}>
            <PaymentProcessing />
        </Box>
    );

    if (loading) {
        return <div className="loading"></div>;
    }

    return (
        <BusinessSettingsMarketplaceProvider>
            <Box className={clsx(classes.container, props.className)}>
                <Box
                    className={clsx(
                        classes.sidebarContainer,
                        { [classes.sidebarContainerMain]: hide && isMobile },
                        {
                            [classes.sidebarContainerVisible]: isMobile && isOpenBusinessSettingsSideBar
                        }
                    )}
                >
                    {sidebar}
                </Box>

                {settingsLoading ? (
                    <Box className={classes.settingsSpinner}>
                        <Spinner />
                    </Box>
                ) : (
                    <Switch>
                        <Redirect
                            exact
                            strict
                            path={`${path}`}
                            to={`${path}/${businessInfoItems.path}/${basicsItem.id}${isMobile ? '?open=settings' : ''}`}
                        />
                        <Route path={`${path}/${businessInfoItems.path}/${basicsItem.id}`}>
                            <span>{basics}</span>
                        </Route>
                        <Route path={`${path}/${businessInfoItems.path}/${locationsItem.id}`}>{locations}</Route>
                        {isRoomsEnabled ? (
                            <Route path={`${path}/${businessInfoItems.path}/${roomsItem.id}`}>{rooms}</Route>
                        ) : (
                            <Redirect
                                from={`${path}/${businessInfoItems.path}/${roomsItem.id}`}
                                to={`${path}/${businessInfoItems.path}/${basicsItem.id}`}
                            />
                        )}
                        <Route path={`${path}/${businessInfoItems.path}/${plansItem.id}`}>{plans}</Route>
                        <Route path={`${path}/${businessInfoItems.path}/${billingItem.id}`}>{billing}</Route>
                        {hasPremiumPlan && (
                            <Route path={`${path}/${businessInfoItems.path}/${textMessagesItem.id}`}>
                                {textMessages}
                            </Route>
                        )}
                        <Route path={`${path}/${businessInfoItems.path}/${additionalInfoItem.id}`}>
                            {additionalInfo}
                        </Route>
                        <Route path={`${path}/${businessInfoItems.path}/${scheduleItem.id}`}>{schedule}</Route>
                        <Route path={`${path}/${businessInfoItems.path}/${logoItem.id}`}>{logos}</Route>
                        <Route path={`${path}/${staffItems.path}/${permissionsItem.id}`}>{permissions}</Route>
                        <Route path={`${path}/${bookingsItems.path}/${onlineBookingItem.id}`}>{onlineBooking}</Route>
                        <Route path={`${path}/${bookingsItems.path}/${scheduleTypeItem.id}`}>{scheduleType}</Route>
                        {/*<Route path={`${path}/${clientsItems.path}/${intakeFormItem.id}`}>{intakeForm}</Route>*/}
                        <Route path={`${path}/${petsItems.path}/${requirementsItem.id}`}>{requirements}</Route>
                        <Route path={`${path}/${petsItems.path}/${typesItem.id}`}>{petTypes}</Route>
                        <Route path={`${path}/${petsItems.path}/${petParentPortalItem.id}`}>{petParentPortal}</Route>
                        <Route path={`${path}/${petsItems.path}/${belongingsItem.id}`}>{belongings}</Route>
                        <Route path={`${path}/${petsItems.path}/${tagsItem.id}`}>{petTags}</Route>
                        <Route path={`${path}/${petsItems.path}/${feedingItem.id}`}>{feeding}</Route>

                        <Route path={`${path}/${paymentsItems.path}/${paymentProcessingItem.id}`}>
                            {paymentProcessing}
                        </Route>

                        <Route path={`${path}/${salesItems.path}/${taxesItem.id}`}>{taxes}</Route>
                        <Route path={`${path}/${salesItems.path}/${tipsItem.id}`}>{tips}</Route>
                        <Route path={`${path}/${salesItems.path}/${depositItem.id}`}>{deposit}</Route>
                        <Route path={`${path}/${salesItems.path}/${invoicesItem.id}`}>{invoices}</Route>

                        <Route path={`${path}/${facilityAndStaffItems.path}/${amenitiesItem.id}`}>{amenities}</Route>
                        <Route path={`${path}/${facilityAndStaffItems.path}/${trainingItem.id}`}>{training}</Route>
                        <Route path={`${path}/${settingsItems.path}/${emailItem.id}`}>{emailNotifications}</Route>
                        <Route path={`${path}/${settingsItems.path}/${integrationsItem.id}`}>{integrations}</Route>
                    </Switch>
                )}
                {/* Conditionally rendered style element */}
                {bodyClass && (
                    <style>{`
					body.${bodyClass} {
						overflow: hidden;
					}
				`}</style>
                )}
            </Box>
        </BusinessSettingsMarketplaceProvider>
    );
};

export default BusinessSettingsComponent;
